import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Home, Building2, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { submitEmail } from '../mock';
import { toast } from 'sonner';
import heroBridge from '../assets/stillwater-lift-bridge.jpg';

const HomePage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await submitEmail(email);
      toast.success('Thank you! We\'ll keep you updated.');
      setEmail('');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const services = [
    {
      icon: Home,
      title: 'Single Family Homes',
      description: 'Comprehensive appraisals for residential properties',
      link: '/services/single-family',
    },
    {
      icon: Building2,
      title: 'Condominiums',
      description: 'Specialized valuations for condo units',
      link: '/services/condominium',
    },
    {
      icon: MapPin,
      title: 'Townhomes & Land',
      description: 'Expert appraisals for townhomes and vacant parcels',
      link: '/services/townhome-land',
    },
  ];

  const benefits = [
    'State Certified Residential Appraiser',
    'Over 5,000 Completed Appraisals',
    'USPAP Compliant Reporting',
    '48-72 Hour Turnaround',
    'FHA/VA Approved',
    'Local Market Expertise',
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroBridge})` }}
          data-testid="home-hero-bg"
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br from-[#2D0A14]/75 via-[#8B1538]/58 to-[#A0252C]/48"></div>
        <div className="absolute inset-0 bg-noise opacity-30"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-40 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-block mb-6 px-4 py-2 bg-white/15 text-white rounded-full text-sm font-semibold border border-white/30 backdrop-blur-sm">
              Trusted Appraisal Services in Twin Cities & Wisconsin
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight drop-shadow-lg">
              Professional Residential
              <span className="block mt-2 text-[#F5E6D3]">
                Appraisal Services
              </span>
            </h1>
            <p className="text-xl text-[#F5E6D3] mb-8 max-w-2xl mx-auto drop-shadow">
              Accurate, timely, and professional property valuations you can trust. Serving homeowners, real estate professionals, and lending institutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/contact">
                <Button size="lg" className="bg-[#D4A574] hover:bg-[#C9A97E] text-[#2D0A14] font-semibold px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all">
                  Get Your Appraisal
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg bg-transparent">
                  View Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-metallic-gold relative">
        <div className="absolute inset-0 bg-noise"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit) => (
              <div
                key={benefit}
                className="flex items-center gap-3 p-4 bg-gradient-to-br from-[#F5E6D3] to-[#E8D5C4] rounded-lg border border-[#D4A574] hover:shadow-md transition-shadow"
              >
                <CheckCircle2 className="w-6 h-6 text-[#A0252C] flex-shrink-0" />
                <span className="font-medium text-gray-800">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-metallic-gold relative">
        <div className="absolute inset-0 bg-noise"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Appraisal Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Specialized expertise for every type of residential property
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <Card
                  key={service.title}
                  className="group hover:shadow-2xl transition-all duration-300 border-2 border-[#E8D5C4] hover:border-[#D4A574] cursor-pointer bg-white"
                >
                  <CardHeader>
                    <div className="w-14 h-14 bg-gradient-to-br from-[#8B1538] to-[#A0252C] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <CardTitle className="text-xl group-hover:text-[#8B1538] transition-colors">
                      {service.title}
                    </CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Link to={service.link}>
                      <Button variant="ghost" className="w-full justify-between group-hover:bg-[#F5E6D3] group-hover:text-[#8B1538]">
                        Learn More
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#8B1538] to-[#A0252C] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white -z-0"></div>
        <div className="absolute inset-0 bg-noise"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-[#F5E6D3]">
            Join our mailing list to receive market insights, tips, and updates on appraisal services.
          </p>
          <form onSubmit={handleEmailSubmit} className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 bg-white/95 border-white text-gray-900 placeholder:text-gray-500 px-4 py-6 text-lg"
              />
              <Button
                type="submit"
                disabled={loading}
                size="lg"
                className="bg-[#D4A574] hover:bg-[#C9A97E] text-gray-900 px-8 py-6 text-lg shadow-lg font-semibold"
              >
                {loading ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default HomePage;